import React, { useMemo, useState } from 'react';
import WorkspacePage from '../components/layout/WorkspacePage';
import Button from '../components/ui/Button';
import StatusBanner from '../components/ui/StatusBanner';
import SkeletonBlock from '../components/ui/SkeletonBlock';
import { API_BASE_URL } from '../config/api';
import { apiRequest } from '../services/apiClient';
import { withQuery } from '../utils/buildQuery';
import { deriveDateRangeForPeriod } from '../utils/periodRange';
import { DIRECTOR_DATA_UPDATED_EVENT } from '../utils/directorEvents';
import './GoalPlanPage.css';

const PERIOD_OPTIONS = ['monthly', 'quarterly', 'yearly'];
const ALL_LOCATIONS = 'ALL';
const ALL_CATEGORIES = 'ALL';

function GoalPlanPage({
  selectedLocationCode = ALL_LOCATIONS,
  selectedPeriod = 'monthly',
  locationLabel = 'Company Rollup'
}) {
  const [periodType, setPeriodType] = useState(PERIOD_OPTIONS.includes(selectedPeriod) ? selectedPeriod : 'monthly');
  const [locationFilter, setLocationFilter] = useState(selectedLocationCode || ALL_LOCATIONS);
  const [categoryFilter, setCategoryFilter] = useState(ALL_CATEGORIES);
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [banner, setBanner] = useState({ kind: '', message: '' });
  const [refreshKey, setRefreshKey] = useState(0);

  const periodStart = deriveDateRangeForPeriod(periodType).startDate;
  const planUrl = useMemo(() => withQuery('/api/goals/plan', {
    periodType,
    periodStart,
    locationCode: locationFilter !== ALL_LOCATIONS ? locationFilter : undefined
  }), [periodType, periodStart, locationFilter]);

  const { data, error, isLoading, refetch } = useGoalPlan(planUrl, refreshKey);
  const locations = useMemo(() => (Array.isArray(data?.locations) ? data.locations : []), [data]);
  const kpis = useMemo(() => (Array.isArray(data?.kpis) ? data.kpis : []), [data]);
  const goals = useMemo(() => (Array.isArray(data?.goals) ? data.goals : []), [data]);

  const categories = useMemo(() => {
    const set = new Set(kpis.map(kpi => kpi.category || 'General'));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [kpis]);

  const visibleKpis = useMemo(() => (
    categoryFilter === ALL_CATEGORIES
      ? kpis
      : kpis.filter(kpi => (kpi.category || 'General') === categoryFilter)
  ), [categoryFilter, kpis]);

  const goalByCell = useMemo(() => {
    const map = new Map();
    goals.forEach(goal => {
      map.set(buildCellKey(goal.locationId, goal.kpiDefinitionId), goal);
    });
    return map;
  }, [goals]);

  const handleRefresh = async () => {
    setBanner({ kind: '', message: '' });
    await refetch();
  };

  const handleDownloadTemplate = async () => {
    const templateUrl = `${API_BASE_URL}${withQuery('/api/goals/import/template', {
      periodType,
      periodStart,
      locationCode: locationFilter !== ALL_LOCATIONS ? locationFilter : undefined
    })}`;
    const response = await fetch(templateUrl, { credentials: 'include' });
    if (!response.ok) {
      setBanner({ kind: 'error', message: 'Unable to download template.' });
      return;
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vizionops-goal-template-${periodType}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setImportLoading(true);
    setImportPreview(null);
    setBanner({ kind: '', message: '' });
    try {
      const contentBase64 = await readFileAsBase64(file);
      const preview = await apiRequest('/api/goals/import/preview', {
        method: 'POST',
        timeoutMs: 30000,
        body: JSON.stringify({
          fileName: file.name,
          contentBase64,
          periodType,
          periodStart
        })
      });
      setImportPreview(preview);
      setBanner({
        kind: preview.summary.errors ? 'warning' : 'success',
        message: `Preview ready: ${preview.summary.creates} create, ${preview.summary.updates} update, ${preview.summary.errors} error.`
      });
    } catch (error) {
      setBanner({ kind: 'error', message: error.message || 'Unable to preview import.' });
    } finally {
      setImportLoading(false);
      event.target.value = '';
    }
  };

  const handleCommitImport = async () => {
    if (!importPreview?.rows?.length) {
      return;
    }

    setImportLoading(true);
    setBanner({ kind: '', message: '' });
    try {
      const result = await apiRequest('/api/goals/import/commit', {
        method: 'POST',
        timeoutMs: 30000,
        body: JSON.stringify({
          periodType,
          periodStart,
          rows: importPreview.rows
        })
      });
      setImportPreview(null);
      setBanner({ kind: 'success', message: `Committed ${result.summary.committed} imported goal rows.` });
      setRefreshKey(current => current + 1);
      window.dispatchEvent(new CustomEvent(DIRECTOR_DATA_UPDATED_EVENT, {
        detail: { source: 'goal-import' }
      }));
    } catch (error) {
      setBanner({ kind: 'error', message: error.message || 'Unable to commit import.' });
    } finally {
      setImportLoading(false);
    }
  };

  const saveCell = async ({ location, kpi, value }) => {
    const trimmed = String(value ?? '').trim();
    const cellKey = buildCellKey(location.id, kpi.id);
    const existing = goalByCell.get(cellKey);
    const currentValue = existing ? String(existing.goalValue) : '';

    if (trimmed === '' || trimmed === currentValue) {
      setDrafts(current => {
        const next = { ...current };
        delete next[cellKey];
        return next;
      });
      return;
    }

    const numericValue = Number(trimmed);
    if (!Number.isFinite(numericValue)) {
      setBanner({ kind: 'error', message: 'Enter a valid numeric goal value.' });
      return;
    }

    setSavingKey(cellKey);
    setBanner({ kind: '', message: '' });
    try {
      const payload = {
        locationId: location.id,
        kpiDefinitionId: kpi.id,
        goalPeriodType: periodType,
        periodStart,
        goalValue: numericValue,
        goalStatus: existing?.goalStatus || 'draft',
        statusNote: existing?.statusNote || 'Updated from Goal Plan.'
      };

      if (existing) {
        await apiRequest(`/api/goals/location/${existing.id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload)
        });
      } else {
        await apiRequest('/api/goals/location', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }

      setDrafts(current => {
        const next = { ...current };
        delete next[cellKey];
        return next;
      });
      setBanner({ kind: 'success', message: `Saved ${kpi.label} for ${location.code}.` });
      setRefreshKey(current => current + 1);
      window.dispatchEvent(new CustomEvent(DIRECTOR_DATA_UPDATED_EVENT, {
        detail: { source: 'goal-plan', locationCode: location.code }
      }));
    } catch (requestError) {
      setBanner({ kind: 'error', message: requestError.message || 'Unable to save goal.' });
    } finally {
      setSavingKey('');
    }
  };

  // Sprint 3B.6: GoalPlanPage migrated to WorkspacePage with
  // width="operational" so the goal-plan grid (location rows × KPI
  // columns) extends to the Sprint 3A 2200px shell on 32"+ monitors.
  // The inline .goal-plan-header section is removed — its contents
  // (eyebrow, title, description, refresh button) are re-mapped onto
  // WorkspacePage slots. The inner .goal-plan-page wrapper survives
  // so the existing grid layout (.goal-plan-toolbar, .goal-plan-import,
  // .goal-plan-grid-shell) keeps applying without a stylesheet
  // rewrite. The .goal-plan-eyebrow / .goal-plan-header CSS rules
  // are now dormant — pure cleanup; no visual change.
  const refreshAction = (
    <Button
      size="sm"
      variant="secondary"
      onClick={handleRefresh}
      loading={isLoading}
      loadingLabel="Refreshing…"
    >
      Refresh
    </Button>
  );

  return (
    <WorkspacePage
      data-workspace-id="goal-plan"
      width="operational"
      eyebrow="Goal Management"
      title="Goal Plan"
      subtitle={`${locationLabel} · plan location targets across KPIs without opening each scorecard row.`}
      actions={refreshAction}
    >
      <div className="goal-plan-page">
      {error ? <StatusBanner kind="error" message={error} onRetry={handleRefresh} retryLabel="Try again" /> : null}
      {banner.message ? <StatusBanner kind={banner.kind} message={banner.message} /> : null}

      <section className="goal-plan-toolbar" aria-label="Goal plan filters">
        <label>
          <span>Period</span>
          <select value={periodType} onChange={(event) => setPeriodType(event.target.value)}>
            {PERIOD_OPTIONS.map(option => (
              <option key={option} value={option}>{capitalize(option)}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Location</span>
          <select value={locationFilter} onChange={(event) => setLocationFilter(event.target.value)}>
            <option value={ALL_LOCATIONS}>All visible locations</option>
            {locations.map(location => (
              <option key={location.id} value={location.code}>{location.code} · {location.name}</option>
            ))}
          </select>
        </label>
        <label>
          <span>KPI category</span>
          <select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}>
            <option value={ALL_CATEGORIES}>All categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </label>
        <div className="goal-plan-period-chip">{periodStart}</div>
      </section>

      <section className="goal-plan-import" aria-label="Goal import">
        <div>
          <h2>Import goals</h2>
          <p>Download a scoped template, upload CSV or XLSX, preview creates and updates, then commit when clean.</p>
        </div>
        <div className="goal-plan-import-actions">
          <Button size="sm" variant="secondary" onClick={handleDownloadTemplate}>
            Download template
          </Button>
          <label className="goal-plan-upload-button">
            <input type="file" accept=".csv,.tsv,.txt,.xlsx" onChange={handleImportFile} disabled={importLoading} />
            {importLoading ? 'Reading file…' : 'Upload CSV/XLSX'}
          </label>
        </div>
      </section>

      {importPreview ? (
        <section className="goal-plan-import-preview">
          <div className="goal-plan-preview-header">
            <div>
              <h2>Import preview</h2>
              <p>
                {importPreview.summary.creates} create · {importPreview.summary.updates} update · {importPreview.summary.errors} error
              </p>
            </div>
            <div className="goal-plan-import-actions">
              <Button size="sm" variant="secondary" onClick={() => setImportPreview(null)} disabled={importLoading}>
                Clear
              </Button>
              <Button
                size="sm"
                onClick={handleCommitImport}
                disabled={importPreview.summary.errors > 0 || importLoading}
                loading={importLoading}
                loadingLabel="Committing…"
              >
                Commit import
              </Button>
            </div>
          </div>
          <div className="goal-plan-preview-table">
            {importPreview.rows.slice(0, 80).map(row => (
              <div className={`goal-plan-preview-row is-${row.action}`} key={row.rowNumber}>
                <span>Row {row.rowNumber}</span>
                <strong>{row.locationCode || 'Unknown'} · {row.kpiKey || 'Unknown KPI'}</strong>
                <span>{row.goalValue}</span>
                <span>{row.action}</span>
                <small>{row.errors?.join(' ') || (row.existingGoalId ? `Was ${row.existingGoalValue}` : 'New goal')}</small>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section className="goal-plan-grid-shell">
        {isLoading && !data ? (
          <GoalPlanSkeleton />
        ) : (
          <div
            className="goal-plan-grid"
            style={{ '--goal-plan-columns': `220px repeat(${Math.max(visibleKpis.length, 1)}, minmax(220px, 1fr))` }}
          >
            <div className="goal-plan-corner">Location</div>
            {visibleKpis.map(kpi => (
              <div className="goal-plan-kpi-header" key={kpi.id}>
                <strong>{kpi.label}</strong>
                <span>{kpi.category || 'General'}</span>
              </div>
            ))}
            {locations.map(location => (
              <React.Fragment key={location.id}>
                <div className="goal-plan-location-cell">
                  <strong>{location.code}</strong>
                  <span>{location.name}</span>
                </div>
                {visibleKpis.map(kpi => {
                  const cellKey = buildCellKey(location.id, kpi.id);
                  const existing = goalByCell.get(cellKey);
                  const draftValue = Object.prototype.hasOwnProperty.call(drafts, cellKey)
                    ? drafts[cellKey]
                    : (existing ? String(existing.goalValue) : '');
                  const isEmpty = !existing && draftValue === '';
                  const isZero = existing && Number(existing.goalValue) === 0;

                  return (
                    <label
                      className={`goal-plan-cell ${isEmpty ? 'is-empty' : ''} ${isZero ? 'is-zero' : ''}`}
                      key={cellKey}
                    >
                      <span>{isEmpty ? 'Empty' : isZero ? 'Zero value' : 'Goal value'}</span>
                      <input
                        inputMode="decimal"
                        value={draftValue}
                        disabled={savingKey === cellKey}
                        placeholder="Empty"
                        onChange={(event) => setDrafts(current => ({ ...current, [cellKey]: event.target.value }))}
                        onBlur={(event) => saveCell({ location, kpi, value: event.target.value })}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.currentTarget.blur();
                          }
                        }}
                      />
                      {existing ? <small>Rev {existing.revisionNumber || 1}</small> : <small>No goal set</small>}
                    </label>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        )}
      </section>
      </div>
    </WorkspacePage>
  );
}

function GoalPlanSkeleton() {
  return (
    <div className="goal-plan-skeleton">
      {Array.from({ length: 8 }).map((_, index) => (
        <SkeletonBlock key={index} height={48} radius="var(--radius-10)" />
      ))}
    </div>
  );
}

function buildCellKey(locationId, kpiDefinitionId) {
  return `${locationId}:${kpiDefinitionId}`;
}

function capitalize(value) {
  return String(value || '').charAt(0).toUpperCase() + String(value || '').slice(1);
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.includes(',') ? result.split(',').pop() : result);
    };
    reader.onerror = () => reject(reader.error || new Error('Unable to read file.'));
    reader.readAsDataURL(file);
  });
}

function useGoalPlan(url, refreshKey) {
  const [state, setState] = React.useState({ data: null, error: '', isLoading: true });

  const load = React.useCallback(async () => {
    setState(current => ({ ...current, isLoading: true, error: '' }));
    try {
      const data = await apiRequest(url);
      setState({ data, error: '', isLoading: false });
      return data;
    } catch (error) {
      setState(current => ({ ...current, error: error.message || 'Unable to load goal plan.', isLoading: false }));
      return null;
    }
  }, [url]);

  React.useEffect(() => {
    load();
  }, [load, refreshKey]);

  return {
    ...state,
    refetch: load
  };
}

export default GoalPlanPage;
